export const FALLBACK_PROPOSAL_LANGUAGE = "en"

export interface ReviewTranslation {
	language: string
	title?: string | null
	content?: string | null
	updatedAt?: number | null
}

export interface ProposalDraft {
	language: string
	title?: string | null
	content?: string | null
	translationStatus: "QUEUED" | "TRANSLATING" | "COMPLETED" | "FAILED"
	failureReason?: string | null
}

export type DraftState =
	| { kind: "proposalLanguage" }
	| { kind: "ready"; title: string | null; content: string }
	| { kind: "pending" }
	| { kind: "failed"; reason: string | null }
	| { kind: "missing" }
	| { kind: "manyProposals"; count: number }

export interface ReviewLanguageState {
	isProposalLanguage: boolean
	hasContent: boolean
	staleLanguages: string[]
}

export function resolveReviewLanguage({
	selectedLanguage,
	proposalLanguage,
	enabledLanguages,
	translations,
}: {
	selectedLanguage: string
	proposalLanguage: string
	enabledLanguages: readonly string[]
	translations: readonly ReviewTranslation[]
}): ReviewLanguageState {
	const withContent = new Set(
		translations
			.filter((t) => (t.content ?? "").trim().length > 0)
			.map((t) => t.language),
	)

	return {
		isProposalLanguage: selectedLanguage === proposalLanguage,
		hasContent: withContent.has(selectedLanguage),
		staleLanguages: enabledLanguages
			.filter((code) => code !== proposalLanguage && withContent.has(code))
			.sort(),
	}
}

export function resolveDraftState({
	selectedLanguage,
	proposalLanguage,
	proposalCount,
	drafts,
}: {
	selectedLanguage: string
	proposalLanguage: string
	proposalCount: number
	drafts: readonly ProposalDraft[]
}): DraftState {
	if (selectedLanguage === proposalLanguage) return { kind: "proposalLanguage" }
	if (proposalCount > 1) return { kind: "manyProposals", count: proposalCount }

	const draft = drafts.find((d) => d.language === selectedLanguage)
	if (!draft) return { kind: "missing" }

	switch (draft.translationStatus) {
		case "COMPLETED":
			if (!(draft.content ?? "").trim()) return { kind: "missing" }
			return {
				kind: "ready",
				title: draft.title ?? null,
				content: draft.content ?? "",
			}
		case "FAILED":
			return { kind: "failed", reason: draft.failureReason ?? null }
		default:
			return { kind: "pending" }
	}
}

export function findTranslation(
	translations: readonly ReviewTranslation[],
	language: string,
): ReviewTranslation | undefined {
	return translations.find((t) => t.language === language)
}

export function reviewLanguageMessage({
	languageName,
	state,
	draft,
}: {
	languageName: string
	state: ReviewLanguageState
	draft: DraftState
}): string | null {
	if (draft.kind === "proposalLanguage") {
		if (state.staleLanguages.length === 0) return null
		return `Accepting updates ${languageName} and the drafts for ${state.staleLanguages.join(", ")}.`
	}
	switch (draft.kind) {
		case "ready":
			return `Drafted ${languageName}. Edit it here, and accepting publishes what you read.`
		case "pending":
			return `Drafting the ${languageName} version. It appears here when it is ready.`
		case "failed":
			return (
				draft.reason ??
				`The ${languageName} draft did not finish, so this shows the published wording.`
			)
		case "manyProposals":
			return `This article has ${draft.count} proposed changes. Accept or skip until one is left to read it in ${languageName}.`
		default:
			return state.hasContent
				? `No ${languageName} draft for this change, so this shows the published article.`
				: `${languageName} has no article yet, so this shows the change in the default language.`
	}
}

export interface ReviewDocument {
	baseHtml: string
	mergedHtml: string
	// False when the view borrows another language's text to stay readable.
	isSelectedLanguage: boolean
}

// What the review body renders. A language with neither a draft nor a
// published article shows the proposal's own diff instead of an empty page.
export function resolveReviewDocument({
	isProposalLanguage,
	draft,
	translationContent,
	proposalBaseHtml,
	proposalMergedHtml,
}: {
	isProposalLanguage: boolean
	draft: DraftState
	translationContent: string | null | undefined
	proposalBaseHtml: string
	proposalMergedHtml: string
}): ReviewDocument {
	const proposal = {
		baseHtml: proposalBaseHtml,
		mergedHtml: proposalMergedHtml,
	}
	if (isProposalLanguage) return { ...proposal, isSelectedLanguage: true }
	if (draft.kind === "ready") {
		return { baseHtml: "", mergedHtml: draft.content, isSelectedLanguage: true }
	}
	const published = translationContent ?? ""
	if (published.trim()) {
		return {
			baseHtml: published,
			mergedHtml: published,
			isSelectedLanguage: true,
		}
	}
	return { ...proposal, isSelectedLanguage: false }
}
